import { DotMatrix } from '@clickguard/design-system'
import { Dialog, Transition } from '@headlessui/react'
import { CreditCardIcon } from '@heroicons/react/outline'
import {
	Elements,
	PaymentElement,
	useElements,
	useStripe
} from '@stripe/react-stripe-js'
import { GButton } from 'components/basic-blocks'
import { useStores } from 'hooks'
import { useThemeStore } from 'layout/navbar/store'
import {
	forwardRef,
	Fragment,
	useEffect,
	useImperativeHandle,
	useRef,
	useState
} from 'react'
import { PaymentMethods } from 'services/user/payment-methods'
import { parseError } from 'utils/errors'
import { useTrackPostHogEvent } from '../../utils/posthog'
import { PostHogEvent, PostHogPath } from '../../utils/posthog-events'
import {
	AddPaymentMethodFormRef,
	AddPaymentMethodProps
} from './types'

type SubmitPhase = 'idle' | 'submitting' | 'verifying_3ds' | 'finalizing'

export const AddPaymentMethod = ({
	open,
	onClose,
	onFinish
}: AddPaymentMethodProps) => {
	const { billingStore } = useStores()
	const { theme } = useThemeStore()
	const {
		accountStore: { account }
	} = useStores()
	const trackPostHogEvent = useTrackPostHogEvent()

	const formRef = useRef<AddPaymentMethodFormRef>(null)
	const submittingRef = useRef(false)
	const [phase, setPhase] = useState<SubmitPhase>('idle')
	const [paymentError, setPaymentError] = useState<string | null>(null)
	const [isPaymentComplete, setIsPaymentComplete] = useState(false)
	// Currently-selected PaymentElement tab. Mirrored from the form's onChange.
	// Drives the "$1 hold" disclaimer visibility — only cards get a pre-auth.
	const [selectedPmType, setSelectedPmType] = useState<string>('card')

	// Reset transient state when the modal re-opens so a previous error / phase
	// doesn't carry across opens.
	useEffect(() => {
		if (open) {
			setPhase('idle')
			setPaymentError(null)
			setSelectedPmType('card')
			setIsPaymentComplete(false)
		}
	}, [open])

	const isBusy = phase !== 'idle'

	const submit = async () => {
		if (submittingRef.current || phase !== 'idle') return
		submittingRef.current = true
		setPhase('submitting')
		setPaymentError(null)

		try {
			// 1. Local validation + create unattached PaymentMethod via Stripe.js.
			const confirmResult = await formRef.current?.confirmPayment()
			if (!confirmResult || confirmResult.kind !== 'ok') {
				// Stripe-side error or aborted — the form has already surfaced the
				// error via onPaymentError (which we wire into setPaymentError).
				return
			}

			// 2. Send to BE: verifyAndAttachPm runs the pre-auth (cards) or
			// mandate SI (non-card debits) before attaching + persisting.
			let result = await PaymentMethods.verify({
				payment_method_id: confirmResult.pmToken,
				account_id: account?.id || null
			})

			// 3. Card needs 3DS — bounce out to handleNextAction, then re-post
			// with payment_intent_id so the BE picks up the same PI.
			if (result.kind === 'requires_action') {
				setPhase('verifying_3ds')
				const next = await formRef.current?.handleNextAction(
					result.client_secret
				)
				if (!next || !next.ok) {
					const rawMsg =
						next && !next.ok ? next.error : 'Card authentication failed'
					const looksCancelled = /cancel|abandon|dismiss/i.test(rawMsg)
					setPaymentError(
						looksCancelled
							? 'Authentication was cancelled — please try again.'
							: rawMsg
					)
					return
				}
				setPhase('finalizing')
				result = await PaymentMethods.verify({
					payment_intent_id: result.payment_intent_id,
					account_id: account?.id || null
				})
				if (result.kind !== 'attached') {
					setPaymentError('Unexpected response after 3DS, please try again')
					return
				}
			}

			if (result.kind === 'attached') {
				trackPostHogEvent(
					PostHogEvent.UPDATE_PAYMENT,
					PostHogPath.SETTINGS_PAYMENT_METHOD,
					null
				)
				// Refresh the parent's PM list so the new method shows up after close.
				await billingStore.getPaymentMethods()
				onFinish && onFinish(result.payment_method)
				onClose()
			}
		} catch (err: any) {
			setPaymentError(parseError(err))
		} finally {
			setPhase('idle')
			submittingRef.current = false
		}
	}

	const submitLabel = (() => {
		if (phase === 'submitting') return 'Processing...'
		if (phase === 'verifying_3ds') return 'Waiting for your bank...'
		if (phase === 'finalizing') return 'Finalizing...'
		return 'Add payment method'
	})()

	return (
		<Transition.Root show={open} as={Fragment}>
			<Dialog
				as="div"
				className="fixed z-50 inset-0 overflow-y-auto"
				onClose={isBusy ? () => undefined : onClose}
			>
				<div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
					<Transition.Child
						as={Fragment}
						enter="ease-out duration-300"
						enterFrom="opacity-0"
						enterTo="opacity-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100"
						leaveTo="opacity-0"
					>
						<Dialog.Overlay className="fixed inset-0 bg-modal-background bg-opacity-75 transition-opacity backdrop-blur-sm" />
					</Transition.Child>

					{/* This element is to trick the browser into centering the modal contents. */}
					<span
						className="hidden sm:inline-block sm:align-middle sm:h-screen"
						aria-hidden="true"
					>
						&#8203;
					</span>
					<Transition.Child
						as={Fragment}
						enter="ease-out duration-300"
						enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
						enterTo="opacity-100 translate-y-0 sm:scale-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100 translate-y-0 sm:scale-100"
						leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
					>
						<div className="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
							<div>
								<div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-primary-100">
									<CreditCardIcon
										className="h-6 w-6 text-primary-600"
										aria-hidden="true"
									/>
								</div>
								<div className="mt-3 sm:mt-5">
									{billingStore.stripe ? (
										<Elements
											stripe={billingStore.stripe}
											options={{
												mode: 'setup',
												currency: 'usd',
												paymentMethodCreation: 'manual',
												// Hide manual entry / microdeposit verification under the
												// us_bank_account tab — the verify round trip can't host
												// microdeposits, so manual entry would just fail at
												// confirmMandateSetupIntent. Only Financial Connections
												// (instant verification) is supported end-to-end.
												paymentMethodOptions: {
													us_bank_account: {
														verification_method: 'instant'
													}
												},
												appearance: {
													theme: theme === 'dark' ? 'night' : 'stripe'
												}
											}}
										>
											<PaymentMethodForm
												ref={formRef}
												onPaymentComplete={setIsPaymentComplete}
												onPaymentError={(e) => setPaymentError(e || null)}
												onSelectedPmTypeChange={setSelectedPmType}
											/>
										</Elements>
									) : (
										<div className="min-h-[200px] flex items-center justify-center">
											<DotMatrix size="sm" />
										</div>
									)}
								</div>
							</div>

							{paymentError && (
								<p className="mt-3 text-sm leading-tight text-red-500 text-center">
									{paymentError}
								</p>
							)}

							<div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
								<GButton
									loading={isBusy}
									disabled={!isPaymentComplete || isBusy}
									label={submitLabel}
									onClick={submit}
									color="primary"
									className="sm:col-start-2"
								/>
								<GButton
									label="Cancel"
									disabled={isBusy}
									onClick={() => onClose()}
									className="sm:col-start-1"
								/>
							</div>

							{/* "$1 hold" disclaimer — only shown when the Card tab is
							    selected. Non-card debits (SEPA, BACS, US bank, etc.)
							    skip the pre-auth on the BE; PaymentElement renders the
							    appropriate mandate disclosure inline for those. */}
							{billingStore.stripe && selectedPmType === 'card' && (
								<p className="mt-3 text-xs leading-tight text-t-secondary text-center">
									A $1 temporary authorization may be placed on your card to
									verify it. It will not be charged.
								</p>
							)}
						</div>
					</Transition.Child>
				</div>
			</Dialog>
		</Transition.Root>
	)
}

interface PaymentMethodFormInternalProps {
	onPaymentComplete?: (isComplete: boolean) => void
	onPaymentError?: (error?: string) => void
	onSelectedPmTypeChange?: (type: string) => void
}

const PaymentMethodForm = forwardRef<
	AddPaymentMethodFormRef,
	PaymentMethodFormInternalProps
>(({ onPaymentComplete, onPaymentError, onSelectedPmTypeChange }, ref) => {
	const stripe = useStripe()
	const elements = useElements()

	useImperativeHandle(ref, () => ({
		confirmPayment: async () => {
			if (!stripe || !elements) return { kind: 'aborted' as const }

			onPaymentError?.(undefined)

			// Deferred-intent / manual PM creation flow: <Elements> is mounted
			// without a clientSecret, so we just validate locally with
			// elements.submit() and then create an unattached PaymentMethod.
			// The BE handles attach + verification (delayed-attach pattern), so
			// failures don't leave orphan PMs on the customer.
			const { error: submitError } = await elements.submit()
			if (submitError) {
				const msg = submitError.message || 'Please check your card details'
				onPaymentError?.(msg)
				return { kind: 'error' as const, error: msg }
			}

			const { error, paymentMethod } = await stripe.createPaymentMethod({
				elements
			})
			if (error) {
				const msg = error.message || 'Unexpected error, please try again'
				onPaymentError?.(msg)
				return { kind: 'error' as const, error: msg }
			}
			if (!paymentMethod) {
				return { kind: 'error' as const, error: 'Card verification failed' }
			}

			return { kind: 'ok' as const, pmToken: paymentMethod.id }
		},
		handleNextAction: async (clientSecret: string) => {
			if (!stripe) return { ok: false as const, error: 'Stripe not ready' }
			const result = await stripe.handleNextAction({ clientSecret })
			if (result.error) {
				return {
					ok: false as const,
					error: result.error.message || 'Card authentication failed'
				}
			}
			return { ok: true as const }
		}
	}))

	return (
		<div className="w-full">
			<PaymentElement
				options={{ layout: 'tabs' }}
				onChange={(event) => {
					onPaymentComplete?.(event.complete)
					if (event.value?.type) {
						onSelectedPmTypeChange?.(event.value.type)
					}
				}}
				onFocus={() => {
					onPaymentError?.(undefined)
				}}
			/>
		</div>
	)
})

PaymentMethodForm.displayName = 'AddPaymentMethodForm'
